import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties, RefObject } from 'react'

export const STICKY_COMPOSER_HEIGHT_VAR = '--assistant-chat-sticky-composer-height'

type StickyComposerReserveStyle = CSSProperties & {
  [STICKY_COMPOSER_HEIGHT_VAR]: string
}

interface StickyComposerReservedHeight {
  surfaceRef: RefObject<HTMLDivElement | null>
  reserveStyle: StickyComposerReserveStyle | undefined
}

export function useStickyComposerReservedHeight(enabled: boolean): StickyComposerReservedHeight {
  const surfaceRef = useRef<HTMLDivElement | null>(null)
  const [reservedHeightPx, setReservedHeightPx] = useState(0)

  useLayoutEffect(() => {
    if (!enabled) {
      setReservedHeightPx(0)
      return
    }

    const surface = surfaceRef.current
    if (!surface || typeof ResizeObserver === 'undefined') {
      return
    }

    const updateHeight = () => {
      setReservedHeightPx(surface.getBoundingClientRect().height)
    }

    updateHeight()
    const observer = new ResizeObserver(updateHeight)
    observer.observe(surface)

    return () => {
      observer.disconnect()
    }
  }, [enabled])

  const reserveStyle = useMemo<StickyComposerReserveStyle | undefined>(() => {
    if (!enabled) return undefined
    return {
      [STICKY_COMPOSER_HEIGHT_VAR]: `${reservedHeightPx}px`,
    }
  }, [enabled, reservedHeightPx])

  return { surfaceRef, reserveStyle }
}
